import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import adminService from '../../services/admin';
import {
  DEFAULT_ADMIN_MENU,
  DEFAULT_ADMIN_QUICK_ACTIONS,
  DEFAULT_MAIN_MENU,
  getDefaultUISettings,
  normalizeUISettings,
} from '../../services/siteSettings';

const DEFAULT_PAYMENT_SETTINGS = {
  provider: 'stripe',
  gateway_url: '',
  public_key: '',
  secret_key: '',
  webhook_secret: '',
  stripe_publishable_key: '',
  stripe_secret_key: '',
  stripe_webhook_secret: '',
  paypal_client_id: '',
  paypal_client_secret: '',
  paypal_webhook_id: '',
  paypal_api_base: 'https://api-m.paypal.com',
  currency: 'USD',
  enabled: false,
};

const DEFAULT_SMTP_SETTINGS = {
  host: '',
  port: 587,
  username: '',
  password: '',
  from_email: '',
  from_name: 'VoxifyAI',
  use_tls: true,
  enabled: false,
};

const formatJson = (value) => JSON.stringify(value, null, 2);

const cloneItems = (items) => items.map((item) => ({ ...item }));

const parseJsonArray = (value, fieldLabel, fallback = []) => {
  if (!value.trim()) {
    return cloneItems(fallback);
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      throw new Error();
    }
    return parsed;
  } catch {
    throw new Error(`${fieldLabel} must be a valid JSON array`);
  }
};

const buildFormState = (
  uiSettings = getDefaultUISettings(),
  payment = DEFAULT_PAYMENT_SETTINGS,
  smtp = DEFAULT_SMTP_SETTINGS
) => ({
  site_name: uiSettings.siteName,
  logo_url: uiSettings.logoUrl,
  favicon_url: uiSettings.faviconUrl,
  site_description: uiSettings.siteDescription,
  seo_title: uiSettings.seoTitle,
  seo_description: uiSettings.seoDescription,
  seo_keywords: uiSettings.seoKeywords,
  seo_og_image: uiSettings.seoOgImage,
  main_menu_json: formatJson(uiSettings.mainMenu),
  admin_menu_json: formatJson(uiSettings.adminMenu),
  quick_actions_json: formatJson(uiSettings.adminQuickActions),
  payment_provider: payment.provider || 'stripe',
  payment_gateway_url: payment.gateway_url,
  payment_public_key: payment.stripe_publishable_key || payment.public_key,
  payment_secret_key: payment.stripe_secret_key || payment.secret_key,
  payment_webhook_secret: payment.stripe_webhook_secret || payment.webhook_secret,
  paypal_client_id: payment.paypal_client_id,
  paypal_client_secret: payment.paypal_client_secret,
  paypal_webhook_id: payment.paypal_webhook_id,
  paypal_api_base: payment.paypal_api_base || 'https://api-m.paypal.com',
  payment_currency: payment.currency,
  payment_enabled: Boolean(payment.enabled),
  smtp_host: smtp.host,
  smtp_port: String(smtp.port ?? 587),
  smtp_username: smtp.username,
  smtp_password: smtp.password,
  smtp_from_email: smtp.from_email,
  smtp_from_name: smtp.from_name,
  smtp_use_tls: Boolean(smtp.use_tls),
  smtp_enabled: Boolean(smtp.enabled),
});

function AdminSettings() {
  const [formData, setFormData] = useState(buildFormState());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTestingStripe, setIsTestingStripe] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getSiteContent();
      const items = data.items || [];

      const uiSettingsItem = items.find((item) => item.key === 'ui_settings');
      const paymentSettingsItem = items.find((item) => item.key === 'payment_gateway_settings');
      const smtpSettingsItem = items.find((item) => item.key === 'smtp_settings');

      const uiSettings = uiSettingsItem ? normalizeUISettings(uiSettingsItem) : getDefaultUISettings();
      const paymentSettings = {
        ...DEFAULT_PAYMENT_SETTINGS,
        ...(paymentSettingsItem?.extra_data || {}),
      };
      const smtpSettings = {
        ...DEFAULT_SMTP_SETTINGS,
        ...(smtpSettingsItem?.extra_data || {}),
      };

      setFormData(buildFormState(uiSettings, paymentSettings, smtpSettings));
    } catch (error) {
      console.error('Failed to load admin settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (field, label) => (event) => {
    const [file] = event.target.files || [];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(`${label} must be an image file`);
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setField(field, reader.result);
      } else {
        toast.error(`Failed to read ${label.toLowerCase()} file`);
      }
      event.target.value = '';
    };
    reader.onerror = () => {
      toast.error(`Failed to read ${label.toLowerCase()} file`);
      event.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const handleStripeTest = async () => {
    setIsTestingStripe(true);
    try {
      const result = await adminService.testStripeConnection();
      if (result?.success) {
        toast.success(result.message || 'Stripe connection successful');
      } else {
        toast.error('Stripe connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stripe connection test failed');
    } finally {
      setIsTestingStripe(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const mainMenu = parseJsonArray(formData.main_menu_json, 'Main Menu', DEFAULT_MAIN_MENU);
      const adminMenu = parseJsonArray(formData.admin_menu_json, 'Admin Menu', DEFAULT_ADMIN_MENU);
      const quickActions = parseJsonArray(
        formData.quick_actions_json,
        'Quick Actions',
        DEFAULT_ADMIN_QUICK_ACTIONS
      );

      await Promise.all([
        adminService.upsertContentByKey('ui_settings', {
          title: formData.site_name.trim() || 'VoxifyAI',
          content: formData.site_description.trim(),
          content_type: 'plain_text',
          meta_title: formData.seo_title.trim() || null,
          meta_description: formData.seo_description.trim() || null,
          meta_keywords: formData.seo_keywords.trim() || null,
          is_published: true,
          extra_data: {
            logo_url: formData.logo_url.trim(),
            favicon_url: formData.favicon_url.trim(),
            seo_og_image: formData.seo_og_image.trim(),
            menu_items: mainMenu,
            admin_menu_items: adminMenu,
            admin_quick_actions: quickActions,
          },
        }),
        adminService.upsertContentByKey('payment_gateway_settings', {
          title: 'Payment Gateway Settings',
          content: '',
          content_type: 'json',
          is_published: false,
          extra_data: {
            provider: formData.payment_provider.trim() || 'stripe',
            gateway_url: formData.payment_gateway_url.trim(),
            public_key: formData.payment_public_key.trim(),
            secret_key: formData.payment_secret_key.trim(),
            webhook_secret: formData.payment_webhook_secret.trim(),
            stripe_publishable_key: formData.payment_public_key.trim(),
            stripe_secret_key: formData.payment_secret_key.trim(),
            stripe_webhook_secret: formData.payment_webhook_secret.trim(),
            paypal_client_id: formData.paypal_client_id.trim(),
            paypal_client_secret: formData.paypal_client_secret.trim(),
            paypal_webhook_id: formData.paypal_webhook_id.trim(),
            paypal_api_base: formData.paypal_api_base.trim(),
            currency: formData.payment_currency.trim() || 'USD',
            enabled: formData.payment_enabled,
          },
        }),
        adminService.upsertContentByKey('smtp_settings', {
          title: 'SMTP Settings',
          content: '',
          content_type: 'json',
          is_published: false,
          extra_data: {
            host: formData.smtp_host.trim(),
            port: Number(formData.smtp_port) || 587,
            username: formData.smtp_username.trim(),
            password: formData.smtp_password.trim(),
            from_email: formData.smtp_from_email.trim(),
            from_name: formData.smtp_from_name.trim(),
            use_tls: formData.smtp_use_tls,
            enabled: formData.smtp_enabled,
          },
        }),
      ]);

      setFormData((prev) => ({
        ...prev,
        main_menu_json: formatJson(mainMenu),
        admin_menu_json: formatJson(adminMenu),
        quick_actions_json: formatJson(quickActions),
      }));

      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-gray-400 mt-1">Manage branding, SEO, menus, payments, and SMTP email settings.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">Branding</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Site Name</label>
              <input
                type="text"
                value={formData.site_name}
                onChange={(e) => setField('site_name', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Logo URL (optional)</label>
              <input
                type="text"
                value={formData.logo_url}
                onChange={(e) => setField('logo_url', e.target.value)}
                className="input w-full"
                placeholder="https://example.com/logo.png or /logo.png"
              />
              <label className="block text-sm text-gray-400 mt-3 mb-1">Upload Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload('logo_url', 'Logo')}
                className="input w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Paste an image URL/path or upload a logo file.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Favicon URL (optional)</label>
              <input
                type="text"
                value={formData.favicon_url}
                onChange={(e) => setField('favicon_url', e.target.value)}
                className="input w-full"
                placeholder="https://example.com/favicon.ico or /favicon.ico"
              />
              <label className="block text-sm text-gray-400 mt-3 mb-1">Upload Favicon</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload('favicon_url', 'Favicon')}
                className="input w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Paste an image URL/path or upload a favicon file.</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Site Description</label>
              <textarea
                value={formData.site_description}
                onChange={(e) => setField('site_description', e.target.value)}
                className="input w-full"
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">SEO</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Title</label>
              <input
                type="text"
                value={formData.seo_title}
                onChange={(e) => setField('seo_title', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Open Graph Image URL</label>
              <input
                type="url"
                value={formData.seo_og_image}
                onChange={(e) => setField('seo_og_image', e.target.value)}
                className="input w-full"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Description</label>
              <textarea
                value={formData.seo_description}
                onChange={(e) => setField('seo_description', e.target.value)}
                className="input w-full"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Keywords (comma separated)</label>
              <textarea
                value={formData.seo_keywords}
                onChange={(e) => setField('seo_keywords', e.target.value)}
                className="input w-full"
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">Menus & Quick Actions</h2>
          <p className="text-sm text-gray-400">
            Use JSON arrays. Leave a field empty to use defaults.
          </p>
          <div className="grid lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Main Menu JSON</label>
              <textarea
                value={formData.main_menu_json}
                onChange={(e) => setField('main_menu_json', e.target.value)}
                className="input w-full font-mono text-xs"
                rows={12}
                placeholder={formatJson(DEFAULT_MAIN_MENU)}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Admin Menu JSON</label>
              <textarea
                value={formData.admin_menu_json}
                onChange={(e) => setField('admin_menu_json', e.target.value)}
                className="input w-full font-mono text-xs"
                rows={12}
                placeholder={formatJson(DEFAULT_ADMIN_MENU)}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Quick Actions JSON</label>
              <textarea
                value={formData.quick_actions_json}
                onChange={(e) => setField('quick_actions_json', e.target.value)}
                className="input w-full font-mono text-xs"
                rows={12}
                placeholder={formatJson(DEFAULT_ADMIN_QUICK_ACTIONS)}
              />
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Payment Gateway</h2>
            <button
              type="button"
              onClick={handleStripeTest}
              className="btn btn-secondary text-sm py-2"
              disabled={isTestingStripe}
            >
              {isTestingStripe ? 'Testing Stripe...' : 'Test Stripe Connection'}
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Provider</label>
              <select
                value={formData.payment_provider}
                onChange={(e) => setField('payment_provider', e.target.value)}
                className="input w-full"
              >
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Gateway URL</label>
              <input
                type="text"
                value={formData.payment_gateway_url}
                onChange={(e) => setField('payment_gateway_url', e.target.value)}
                className="input w-full"
                placeholder="https://api.stripe.com or https://api-m.paypal.com"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Stripe Public Key</label>
              <input
                type="text"
                value={formData.payment_public_key}
                onChange={(e) => setField('payment_public_key', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Stripe Secret Key</label>
              <input
                type="password"
                value={formData.payment_secret_key}
                onChange={(e) => setField('payment_secret_key', e.target.value)}
                className="input w-full"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Stripe Webhook Secret</label>
              <input
                type="password"
                value={formData.payment_webhook_secret}
                onChange={(e) => setField('payment_webhook_secret', e.target.value)}
                className="input w-full"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Currency</label>
              <input
                type="text"
                value={formData.payment_currency}
                onChange={(e) => setField('payment_currency', e.target.value.toUpperCase())}
                className="input w-full"
                maxLength={10}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">PayPal Client ID</label>
              <input
                type="text"
                value={formData.paypal_client_id}
                onChange={(e) => setField('paypal_client_id', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">PayPal Client Secret</label>
              <input
                type="password"
                value={formData.paypal_client_secret}
                onChange={(e) => setField('paypal_client_secret', e.target.value)}
                className="input w-full"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">PayPal Webhook ID</label>
              <input
                type="text"
                value={formData.paypal_webhook_id}
                onChange={(e) => setField('paypal_webhook_id', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">PayPal API Base URL</label>
              <input
                type="text"
                value={formData.paypal_api_base}
                onChange={(e) => setField('paypal_api_base', e.target.value)}
                className="input w-full"
                placeholder="https://api-m.paypal.com"
              />
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.payment_enabled}
              onChange={(e) => setField('payment_enabled', e.target.checked)}
            />
            <span className="text-sm">Payment gateway enabled</span>
          </label>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">SMTP Email</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">SMTP Host</label>
              <input
                type="text"
                value={formData.smtp_host}
                onChange={(e) => setField('smtp_host', e.target.value)}
                className="input w-full"
                placeholder="smtp.example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">SMTP Port</label>
              <input
                type="number"
                value={formData.smtp_port}
                onChange={(e) => setField('smtp_port', e.target.value)}
                className="input w-full"
                min={1}
                max={65535}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">SMTP Username</label>
              <input
                type="text"
                value={formData.smtp_username}
                onChange={(e) => setField('smtp_username', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">SMTP Password</label>
              <input
                type="password"
                value={formData.smtp_password}
                onChange={(e) => setField('smtp_password', e.target.value)}
                className="input w-full"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">From Email</label>
              <input
                type="email"
                value={formData.smtp_from_email}
                onChange={(e) => setField('smtp_from_email', e.target.value)}
                className="input w-full"
                placeholder="noreply@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">From Name</label>
              <input
                type="text"
                value={formData.smtp_from_name}
                onChange={(e) => setField('smtp_from_name', e.target.value)}
                className="input w-full"
                placeholder="VoxifyAI"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.smtp_use_tls}
                onChange={(e) => setField('smtp_use_tls', e.target.checked)}
              />
              <span className="text-sm">Use TLS</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.smtp_enabled}
                onChange={(e) => setField('smtp_enabled', e.target.checked)}
              />
              <span className="text-sm">SMTP enabled</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary min-w-40" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminSettings;
